import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { changeOwnPassword, validateNewPassword, authMessage } from '../../utils/passwords';
import './Auth.css';

const Account = () => {
  const { user, role, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loadingData, setLoadingData] = useState(true);

  // ── Change password ──
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNext, setPwNext] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwShow, setPwShow] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    if (loading) return; // Wait for auth to resolve
    if (!user) { navigate('/login'); return; }
    setName(user.displayName || '');
    // Load extra fields from Firestore
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setPhone(data.phone || '');
        }
      } catch (err) {
        console.warn('Could not load user data:', err);
      }
      setLoadingData(false);
    })();
  }, [user, loading, navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await updateProfile(auth.currentUser, { displayName: name });
      try {
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000));
        await Promise.race([
          // Never `role`: it's owner/admin-managed and the rules reject a
          // self-edit of it (which would fail the whole save).
          setDoc(doc(db, 'users', user.uid), {
            name,
            email: user.email,
            phone,
            updatedAt: new Date().toISOString(),
          }, { merge: true }),
          timeout
        ]);
      } catch (fsErr) {
        console.warn('Firestore save failed/timed out:', fsErr);
      }
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
      console.error(err);
    }
    setSaving(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const invalid = !pwCurrent ? 'Enter your current password.' : validateNewPassword(pwNext, pwConfirm, pwCurrent);
    if (invalid) { setPwMsg({ type: 'error', text: invalid }); return; }
    setPwBusy(true);
    setPwMsg({ type: '', text: '' });
    try {
      const others = await changeOwnPassword(auth.currentUser, pwCurrent, pwNext);
      setPwCurrent(''); setPwNext(''); setPwConfirm(''); setPwShow(false);
      setPwMsg({
        type: 'success',
        text: others
          ? `Password changed. Signed out your ${others} other device${others === 1 ? '' : 's'}.`
          : 'Password changed. Any other devices will be signed out.',
      });
    } catch (err) {
      setPwMsg({ type: 'error', text: authMessage(err, 'Could not change the password. Please try again.') });
    }
    setPwBusy(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading || !user) return null;

  return (
    <div className="auth">
      <div className="auth__card" style={{ maxWidth: 460 }}>
        <div className="auth__logo"><span>Desi Bites</span></div>
        <h1 className="auth__title">My Account</h1>
        <p className="auth__sub">Manage your profile</p>

        {loadingData ? (
          <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>Loading…</p>
        ) : (
          <>
            {error && <div className="auth__error">{error}</div>}
            {success && <div className="acct__success">{success}</div>}

            <form className="auth__form" onSubmit={handleSave}>
              <div className="auth__field">
                <label className="auth__label">Email</label>
                <input
                  className="auth__input"
                  type="email"
                  value={user.email}
                  disabled
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                />
              </div>
              <div className="auth__field">
                <label className="auth__label">Full Name</label>
                <input
                  className="auth__input"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="auth__field">
                <label className="auth__label">Phone Number <span style={{ opacity: 0.4, fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                <input
                  className="auth__input"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <button className="auth__btn" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </form>

            <div className="acct__pw">
              {!pwOpen ? (
                <button type="button" className="acct__pw-toggle" onClick={() => { setPwOpen(true); setPwMsg({ type: '', text: '' }); }}>
                  🔑 Change password
                </button>
              ) : (
                <form className="auth__form" onSubmit={handleChangePassword}>
                  <div className="acct__pw-head">
                    <span>🔑 Change password</span>
                    <button type="button" className="acct__pw-cancel" onClick={() => { setPwOpen(false); setPwCurrent(''); setPwNext(''); setPwConfirm(''); setPwMsg({ type: '', text: '' }); }}>
                      Cancel
                    </button>
                  </div>
                  {pwMsg.type === 'error' && <div className="auth__error">{pwMsg.text}</div>}
                  <div className="auth__field">
                    <label className="auth__label" htmlFor="acct-pw-current">Current password</label>
                    <input id="acct-pw-current" className="auth__input" type={pwShow ? 'text' : 'password'} autoComplete="current-password"
                      value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} />
                  </div>
                  <div className="auth__field">
                    <label className="auth__label" htmlFor="acct-pw-new">New password</label>
                    <input id="acct-pw-new" className="auth__input" type={pwShow ? 'text' : 'password'} autoComplete="new-password" placeholder="At least 6 characters"
                      value={pwNext} onChange={(e) => setPwNext(e.target.value)} />
                  </div>
                  <div className="auth__field">
                    <label className="auth__label" htmlFor="acct-pw-confirm">Confirm new password</label>
                    <input id="acct-pw-confirm" className="auth__input" type={pwShow ? 'text' : 'password'} autoComplete="new-password"
                      value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} />
                  </div>
                  <label className="acct__pw-show">
                    <input type="checkbox" checked={pwShow} onChange={(e) => setPwShow(e.target.checked)} /> Show passwords
                  </label>
                  <p className="acct__pw-note">Your other devices will be signed out. This one stays signed in.</p>
                  <button className="auth__btn" type="submit" disabled={pwBusy}>
                    {pwBusy ? 'Changing…' : 'Change password'}
                  </button>
                </form>
              )}
              {pwMsg.type === 'success' && <div className="acct__success" style={{ marginTop: 12 }}>{pwMsg.text}</div>}
            </div>

            <div style={{ marginTop: 20, textAlign: 'center', display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              {['owner', 'admin'].includes(role) && (
                <Link to="/admin" className="auth__btn" style={{ textDecoration: 'none', fontSize: 12, padding: '10px 24px' }}>
                  👥 Manage Users
                </Link>
              )}
              {['owner', 'admin', 'manager'].includes(role) && (
                <Link to="/shifts" className="auth__btn" style={{ textDecoration: 'none', fontSize: 12, padding: '10px 24px' }}>
                  ⏱️ Shift Dashboard
                </Link>
              )}
              {['owner', 'admin', 'manager', 'employee'].includes(role) && (
                <Link to="/clock" className="auth__btn" style={{ textDecoration: 'none', fontSize: 12, padding: '10px 24px', background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}>
                  🕐 Clock In/Out
                </Link>
              )}
              <button className="acct__logout" onClick={handleLogout}>
                Sign Out
              </button>
            </div>

            <div className="acct__meta">
              <span>UID: {user.uid}</span>
              <span>Joined: {user.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : '—'}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Account;
